import { useEffect, useMemo, useState } from "react";
import { MASTERY_AXES, MASTERY_MAX_POINTS, computeMastery } from "@riseafrica/scoring";
import type { QuizQuestion } from "../content/copy";
import { CONFIG } from "../config";
import { dateFr } from "../lib/format";
import { track } from "../lib/track";
import { useApp } from "../state/AppState";
import type { Progression } from "../state/AppState";
import { Fil } from "../components/Fil";

/**
 * Demande au serveur d'émettre l'attestation, et rend son numéro.
 *
 * **Ce que cette fonction remplace.** Le numéro était fabriqué ici, en local :
 * `RA-` suivi d'un horodatage en base 36. Il n'était enregistré nulle part, et
 * il ne respectait même pas le format que la vérification exige — toute
 * personne saisissant son numéro sur la page de contrôle lisait « ce numéro
 * n'a pas le format d'une attestation RISEAFRICA », y compris pour un numéro
 * que le produit venait de lui donner. C'est exactement l'attestation non
 * vérifiable que la règle non négociable n° 6 interdit.
 *
 * Les **réponses** partent, jamais les points : le serveur recalcule. Une
 * attestation dont le porteur choisit la note ne vaut rien pour celui qui la
 * lit, et c'est lui qu'elle sert.
 *
 * Rend `null` si l'émission échoue. On n'affiche alors aucun numéro : une
 * attestation sans numéro est une déception, un numéro qui ne se vérifie pas
 * est un mensonge.
 */
async function emettreAttestation(
  reponses: readonly number[],
  nom: string,
  metier: string | null,
  precedent: string,
): Promise<{ serial: string | null; progression: Progression | null }> {
  const vide = { serial: null, progression: null };
  try {
    const r = await fetch(CONFIG.attestationEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reponses, nom, metier, precedent }),
    });
    if (!r.ok) return vide;
    const corps = (await r.json()) as { serial?: string; progression?: Progression | null };
    return {
      serial: typeof corps.serial === "string" ? corps.serial : null,
      progression: corps.progression ?? null,
    };
  } catch {
    return vide;
  }
}

/** Volet 03 — les mises en situation, et le nom porté sur l'attestation. */
export function Volet3() {
  /*
    Ouverture et achèvement, comptés séparément.

    Le volet est passé de huit à seize questions le 12/08 pour rendre sa mesure
    moins facile à tromper. Ce gain a un prix en écrans, et ce prix restait
    invisible : rien ne comptait ceux qui renoncent en route. Désigner un
    indicateur qu'on ne mesure pas revient à ne rien surveiller.
  */
  useEffect(() => {
    track("volet3_start");
  }, []);

  const { goTo, setT3, say, t1, t2, aSonCompte } = useApp();

  /** Dernière étape réellement franchie, vers laquelle le retour ramène. */
  const retour: { vue: "s-r2" | "s-r1"; libelle: string } | null = t2
    ? { vue: "s-r2", libelle: "Revenir au coût de mon temps" }
    : t1
      ? { vue: "s-r1", libelle: "Revenir à mon indice" }
      : null;

  /**
   * Les huit situations, réécrites dans le métier de la personne quand le
   * volet 1 les a produites. Les axes mesurés et le barème ne changent pas :
   * seul l'habillage suit le métier, sans quoi deux « niveau 2 » ne seraient
   * plus comparables. Le générique reste le repli — entrée directe par ce
   * volet, échec du modèle, ou plafond de dépense atteint.
   */
  const situations = t1?.situations ?? null;
  const surMesure = situations !== null && situations.length === MASTERY_AXES.length;

  /*
    Seize questions : deux par axe, dans l'ordre que le barème attend.

    La première peut être réécrite dans le métier — c'est ce que le modèle
    produit, une situation par axe. **La seconde ne l'est jamais** : c'est la
    question témoin, et un témoin habillé au métier cesserait d'être comparable
    d'une personne à l'autre. Elle vient donc toujours de `MASTERY_AXES`.

    L'appariement compte : `computeMastery` lit les réponses deux à deux pour
    repérer les contradictions. Intervertir cet ordre reviendrait à comparer des
    axes différents entre eux.
  */
  const brutes: ReadonlyArray<QuizQuestion> = MASTERY_AXES.flatMap((axe, i) => {
    const surMesureCetAxe = surMesure && situations !== null ? situations[i] : null;
    return [
      surMesureCetAxe ?? { q: axe.q, o: axe.o.map((t: string, p: number) => [t, p] as const) },
      { q: axe.q2, o: axe.o2.map((t: string, p: number) => [t, p] as const) },
    ];
  });

  /*
    L'ordre des réponses est mélangé, question par question.

    Les trois options étaient toujours rangées de la moins mûre à la plus mûre,
    et la plus mûre était donc toujours la dernière. N'importe qui voyait la
    bonne réponse sans lire : on mesurait ce que la personne sait qu'il faut
    répondre, pas ce qu'elle fait. La consigne « répondez comme vous faites
    vraiment » ne corrigeait pas ce biais — elle l'avouait.

    Rien du barème ne change : les points voyagent avec leur libellé dans le
    couple `[texte, points]`, c'est l'affichage seul qui bouge. Le calcul reste
    celui du code, comme le score.

    Le mélange est figé au montage — `useMemo` sans dépendance. S'il se rejouait
    à chaque rendu, les options se déplaceraient sous le doigt à chaque réponse
    cochée, ce qui serait pire que le défaut corrigé.
  */
  const questions = useMemo<ReadonlyArray<QuizQuestion>>(
    () =>
      brutes.map((question) => {
        const options = [...question.o];
        for (let i = options.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [options[i], options[j]] = [options[j], options[i]];
        }
        return { ...question, o: options };
      }),
    // Figé volontairement : le mélange ne doit pas rejouer d'un rendu à l'autre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [reponses, setReponses] = useState<Record<number, number>>({});
  const [nom, setNom] = useState("");
  const [precedent, setPrecedent] = useState("");
  const [incomplet, setIncomplet] = useState(false);

  const valider = async () => {
    const manquantes = questions.some((_, i) => reponses[i] === undefined);
    if (manquantes) {
      setIncomplet(true);
      say(`Répondez aux ${brutes.length} questions pour obtenir votre niveau`);
      return;
    }
    setIncomplet(false);
    const points = questions.map((_, i) => reponses[i] as number);
    const resultat = computeMastery(points);
    const porteur = nom.trim() === "" ? "Attestation sans nom" : nom.trim();
    const emise = await emettreAttestation(points, porteur, t1?.metier ?? null, precedent.trim());
    setT3({
      ...resultat,
      nom: porteur,
      serial: emise.serial,
      progression: emise.progression,
      date: dateFr(),
    });
    say(`${resultat.level.libelle} — ${resultat.points} points sur ${MASTERY_MAX_POINTS}`);
    track("volet3_done");
    goTo("s-r3");
  };

  return (
    <section>
      <div className="shell narrow app">
        <Fil etape={3} />
        {/*
          « Votre niveau réel » promettait plus que l'instrument ne tient : huit
          questions déclaratives ne mesurent pas une pratique réelle, elles
          recueillent ce que la personne dit de sa pratique. Le nom arrêté du
          volet est « niveau de maîtrise » ; l'écran avait dérivé tout seul vers
          une promesse qu'un contradicteur n'aurait eu aucun mal à démonter.
        */}
        <h2>Votre niveau de maîtrise</h2>
        {/*
          Le nombre annoncé est **compté**, jamais écrit à la main.

          L'écran disait « huit situations » alors que le volet en pose seize
          depuis qu'il a doublé pour rendre sa mesure moins facile à tromper. La
          personne s'engageait sur huit et en trouvait le double : c'est un faux
          signal, au sens exact de la règle non négociable n° 6, et le pire des
          endroits pour en donner un — celui où l'on demande de répondre
          sincèrement.

          En le tirant de `questions.length`, la phrase ne peut plus mentir : le
          jour où le volet change encore de taille, elle change avec lui.
        */}
        <p className="sub">
          {questions.length} situations
          {surMesure ? ` tirées de votre métier — ${t1?.metier}` : ""}. Répondez comme vous faites
          vraiment, pas comme il faudrait faire.
        </p>
        {/*
          Ce que cette mesure ne fait pas, dit avant qu'on y réponde.

          Les deux premiers volets s'appuient sur une source publiée et sur des
          heures déclarées tâche par tâche ; celui-ci ne s'appuie que sur des
          déclarations. Le taire serait laisser croire à la même solidité, et
          offrir la prise la plus facile à qui voudrait disqualifier l'ensemble.
          L'ordre des réponses est mélangé — c'est la seule protection réelle
          contre la réponse que l'on donne pour bien paraître, et elle vaut
          d'être annoncée.
        */}
        <p className="fine">
          Cette partie est déclarative : elle recueille ce que vous dites de votre pratique, et non
          ce qu'elle est. L'ordre des réponses est mélangé pour qu'aucune ne se repère à sa place.
          Les deux premiers volets, eux, reposent sur une source publiée et sur les heures que vous
          avez déclarées.
        </p>

        {/*
          Le volet arrivait sans rien dire de ce qui le précédait, alors que ses
          huit situations sont visiblement écrites dans le métier de la personne.
          Contenu sur mesure, cadre anonyme : c'est ce décalage qui faisait lire
          les trois volets comme trois questionnaires côte à côte.
        */}
        <div className="guide">
          {surMesure ? (
            <>
              <b>Les deux premiers volets ont mesuré votre {aSonCompte ? "activité" : "poste"}.</b> Celui-ci mesure ce que vous, vous
              en faites déjà. C'est le seul des trois qui ne dépend pas de votre métier mais de votre
              pratique — et c'est lui qui décide si vos dix actions sont écrites pour quelqu'un qui
              débute ou pour quelqu'un qui itère déjà.
            </>
          ) : (
            <>
              <b>Ce volet mesure votre pratique, pas votre poste.</b> Il se passe seul, mais il prend
              son sens à côté des deux autres : l'exposition de votre métier, et ce que ce temps vous
              coûte.
            </>
          )}
        </div>

        <div style={incomplet ? { outline: "2px solid var(--high)" } : undefined}>
          {/*
            Structure de la maquette : un `fieldset` ferait sortir la question
            de la bordure de la carte. `role="radiogroup"` + `aria-labelledby`
            donne la même sémantique au lecteur d'écran.
          */}
          {questions.map((question, i) => (
            <div className="qcard" key={question.q}>
              <div className="q" id={`q-label-${i}`}>
                {i + 1}. {question.q}
              </div>
              <div role="radiogroup" aria-labelledby={`q-label-${i}`}>
                {question.o.map(([libelle, points]) => (
                  <label className={`opt${reponses[i] === points ? " on" : ""}`} key={libelle}>
                    <input
                      type="radio"
                      name={`q${i}`}
                      value={points}
                      checked={reponses[i] === points}
                      onChange={() => setReponses((r) => ({ ...r, [i]: points }))}
                    />
                    <span>{libelle}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        {incomplet && (
          <div className="err">
            Il reste des questions sans réponse. Le niveau se calcule sur les seize.
          </div>
        )}

        <div className="field" style={{ marginTop: "20px" }}>
          <label htmlFor="who">Nom à porter sur l'attestation — facultatif</label>
          <input
            type="text"
            id="who"
            placeholder="Prénom et nom"
            autoComplete="off"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
          />
          <p className="fine" style={{ marginTop: "8px" }}>
            Ce nom sert uniquement à imprimer votre attestation. Laissez le champ vide si vous
            préférez : votre niveau s'affiche quand même.
          </p>
        </div>

        {/*
          Le numéro de la fois précédente, s'il y en a eu une.

          C'est ce qui transforme l'attestation en série plutôt qu'en
          instantané, et c'est la seule raison de revenir que le produit
          offrait — il n'en avait aucune. Ni compte, ni mot de passe, ni donnée
          personnelle : le numéro de série est déjà une clé anonyme, et c'est la
          personne qui l'apporte.

          L'écart est calculé par le serveur, à partir des points qu'il a
          lui-même enregistrés. Une progression qu'on pourrait s'attribuer ne
          vaudrait pas plus que la note qu'on s'attribue.
        */}
        <div className="field" style={{ marginTop: "18px" }}>
          <label htmlFor="precedent">Vous aviez déjà une attestation ? Son numéro — facultatif</label>
          <input
            type="text"
            id="precedent"
            placeholder="RA-2026-XXXX-XXXX"
            autoComplete="off"
            value={precedent}
            onChange={(e) => setPrecedent(e.target.value.toUpperCase())}
          />
          <p className="fine" style={{ marginTop: "8px" }}>
            Vous verrez ce que vous avez gagné depuis. Numéro inconnu ou champ vide : votre niveau
            s'affiche normalement, sans comparaison.
          </p>
        </div>

        <button type="button" className="btn btn-brass" onClick={() => void valider()}>
          Obtenir mon niveau
        </button>
        {/* Même règle qu'au volet 2 : le retour ramène à l'étape précédente
            réellement franchie, et l'annonce. */}
        {retour === null ? (
          <button type="button" className="back" onClick={() => goTo("home")}>
            ← Revenir au diagnostic
          </button>
        ) : (
          <button type="button" className="back" onClick={() => goTo(retour.vue)}>
            ← {retour.libelle}
          </button>
        )}
      </div>
    </section>
  );
}
